import { createTheme } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/styles';
import { amber, green } from '@material-ui/core/colors';

function noneBoxShadow() {
  const res = {};

  Array(25)
    .fill(0)
    .map((item, index) => (res['elevation' + index] = { boxShadow: 'none' }));

  return res;
}

const shadowStyles = noneBoxShadow();

const defaultTextColor = '#2D2D3C';
const secondaryTextColor = '#FA6428';
const disableTextColor = '#808286';

const defaultWhiteColor = 'rgba(255, 255, 255, 1)';

const defaultPrimaryColor = '#282D3C';
const defaultSecondaryColor = '#FA6428';
const defaultBackground = '#F9F9FB';
const defaultErrorColor = '#E72E2E';

const defaultFontSize = 18;

const blankTheme = createTheme({
  palette: {
    text: {
      primary: defaultTextColor,
      secondary: secondaryTextColor,
      disabled: disableTextColor,
    },
    background: {
      paper: defaultWhiteColor,
      default: defaultWhiteColor,
    },
    primary: {
      main: defaultPrimaryColor,
      default: defaultPrimaryColor,
    },
    secondary: {
      main: defaultSecondaryColor,
      default: defaultSecondaryColor,
    },
    error: {
      main: defaultErrorColor,
      default: defaultErrorColor,
    },
  },
  shape: {
    borderRadius: 0,
  },
  typography: {
    fontFamily: 'Whyte;',
    fontStyle: 'normal',
    fontDisplay: 'swap',
    fontWeight: 400,
    // fontSize: '16px',
    color: defaultTextColor,
    allVariants: {
      lineHeight: '24px',
    },
    body1: {
      fontSize: '14px',
      fontWeight: '600',
    },
    h1: {
      lineHeight: '80px',
      fontSize: '68px',
    },
    h2: {
      fontSize: '42px',
      lineHeight: '48px',
      fontWeight: '500',
    },
    h3: {
      fontSize: '28px',
      fontWeight: '500',
    },
    h4: {
      fontSize: '16px',
    },
    h5: {
      fontSize: '14px',
    },
    h6: {
      fontSize: '12px',
    },
    subtitle1: {
      fontSize: '18px',
    },
    subtitle2: {
      fontSize: '14px',
      color: '#808286',
    },
    caption: {
      fontSize: '20px',
      fontWeight: '500',
      textAlign: 'center',
    },
  },
});

const ingotTheme = createTheme({
  ...blankTheme,
  overrides: {
    MuiFormHelperText: {
      root: {
        fontSize: '14px',
        textAlign: 'left',
        '&$error': {
          color: '#F2323F',
        },
      },
    },
    MuiGrid: {
      item: {
        width: '100%',
      },
    },
    MuiFormLabel: {
      root: {
        color: '#565662',
        '&[class*=Mui-error]': {
          color: '#565662',
        },
        '&$disabled': {
          color: '#565662',
        },
      },
    },
    MuiPaper: {
      root: {
        padding: blankTheme.spacing(2),
        // boxShadow: 'none',
      },
      ...shadowStyles,
    },
    MuiExpansionPanelSummary: {
      root: {
        padding: 0,
      },
    },
    MuiTypography: {
      root: {
        color: blankTheme.palette.text.primary,
      },
    },
    MuiInputBase: {
      input: {
        minHeight: '25px',
        fontSize: '24px',
        LineHeight: '28px',
        font: 'initial',
        color: blankTheme.palette.text.primary,
      },
      root: {
        borderRadius: '4px',
        // '&[class*=Mui-error]': {
        //     backgroundColor: '#FCEAEA',
        // },
        color: blankTheme.palette.text.primary,
        borderColor: blankTheme.palette.secondary.main,
        fontSize: defaultFontSize,
        fontWeight: 500,
        '&:hover': {
          color: blankTheme.palette.text.primary,
          borderColor: blankTheme.palette.primary.main,
        },
        '&$focused': {
          color: blankTheme.palette.text.primary,
          borderColor: blankTheme.palette.primary.main,
        },
        '&$disabled': {
          color: blankTheme.palette.text.disabled,
          borderColor: blankTheme.palette.secondary.main,
          '&:hover': {
            color: blankTheme.palette.text.disabled,
            borderColor: blankTheme.palette.secondary.main,
          },
        },
      },
    },
    MuiChip: {
      outlined: {
        color: blankTheme.palette.primary.main,
        borderColor: blankTheme.palette.primary.main,
      },
      root: {
        fontSize: defaultFontSize,
        paddingTop: 16,
        paddingBottom: 16,
      },
    },
    MuiFilledInput: {
      root: {
        backgroundColor: defaultBackground,
      },
    },
    MuiOutlinedInput: {
      notchedOutline: {
        color: blankTheme.palette.text.primary,
        borderColor: blankTheme.palette.primary.main,
      },
      root: {
        color: blankTheme.palette.text.primary,
        borderColor: blankTheme.palette.primary.main,
        '&:hover': {
          color: blankTheme.palette.text.primary,
          borderColor: blankTheme.palette.primary.main,
        },
        '&$focused': {
          color: blankTheme.palette.text.primary,
          borderColor: blankTheme.palette.primary.main,
        },
      },
    },
    MuiAppBar: {
      colorPrimary: {
        color: '#FFFFFF',
        backgroundColor: '#FFFFFF',
      },
      root: {
        color: '#F3F4F7',
        flexGrow: 1,
      },
    },
    MuiListItem: {
      root: {
        '&.Mui-selected': {
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
      button: {
        '&:hover': {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiListItemIcon: {
      root: {
        color: blankTheme.palette.primary.main,
      },
    },
    MuiIconButton: {
      root: {
        color: blankTheme.palette.text.primary,
        justifyContent: 'space-between',
        borderRadius: 0,
      },
    },
    MuiSvgIcon: {
      colorPrimary: {
        color: '#FA6428',
      },
      root: {
        color: blankTheme.palette.text.primary,
      },
    },
    MuiFormControl: {
      root: {
        height: '58px',
        marginTop: blankTheme.spacing(2),
        marginBottom: blankTheme.spacing(2),
      },
    },
    MuiButton: {
      root: {
        borderRadius: '4px',
        textTransform: 'none',
      },
      containedPrimary: {
        backgroundColor: '#FA6428',
        color: '#FFFFFF',
        justifyContent: 'center',
        '& svg': {
          color: '#FFFFFF',
        },
        '&:hover': {
          '& svg': {
            color: '#FA6428',
          },
        },
      },
      containedSecondary: {
        justifyContent: 'space-between',
        '&:hover': {
          backgroundColor: '#FA6428',
          color: '#FFFFFF',
        },
        border: '1px solid #FA6428',
        backgroundColor: '#FFFFFF',
        color: '#FA6428',
      },
    },
    MuiPopover: {
      paper: {
        boxShadow:
          '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
      },
    },
    MuiFab: {
      root: {
        backgroundColor: '#FFFFFF',
        color: '#FFFFFF',
      },
    },
    MuiDialog: {
      root: {
        borderRadius: '4px',
      },
    },
  },
});

const outerTheme = createTheme({
  ...ingotTheme,
  link: {
    color: 'inherit',
    textDecoration: 'none',
  },
  icon: {
    width: 20,
    height: 20,
    margin: 2,
  },
  formIcon: {
    position: 'relative',
    top: 5,
  },
  RefInput: {
    '& $button': {
      display: 'none',
    },
    '&:hover': {
      '& $button': {
        display: 'inline',
      },
    },
  },
  textAlignCenter: {
    textAlign: 'center',
  },
  inputDynamic: {
    width: 'calc(100% - 15px)',
  },
});

function MainTheme(props) {
  return (
    <ThemeProvider theme={outerTheme}>
      <ThemeProvider
        theme={(theme) =>
          createTheme({
            // Nested Theme
            ...theme,
            outlinedText: {
              borderColor: 'rgba(0, 0, 0, 0.23)',
              borderStyle: 'solid',
              borderWidth: 1,
              padding: 9,
              marginLeft: 15,
              marginRight: 8,
            },
            snackSuccess: {
              backgroundColor: green[600],
              opacity: 0.9,
              marginRight: theme.spacing(1),
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
            },
            snackError: {
              backgroundColor: theme.palette.error.dark,
              opacity: 0.9,
              marginRight: theme.spacing(1),
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
            },
            snackWarn: {
              backgroundColor: amber[700],
              opacity: 0.9,
              marginRight: theme.spacing(1),
              fontSize: 20,
              display: 'flex',
              alignItems: 'center',
            },
            editForm: {
              root: {
                width: '100%',
              },
              paper: {
                padding: theme.spacing(2),
                color: theme.palette.text.secondary,
              },
              RefInput: theme.RefInput,
              link: theme.link,
              inputDynamic: theme.inputDynamic,
            },
            justifySpaceAround: {
              justifyContent: 'space-around',
            },
            link: {
              color: '#FA6428',
              textDecoration: 'underline',
            },
            linkText: {
              color: 'black',
              textDecoration: 'none',
            },
            loginBackground: {
              top: '0',
              left: '0',
              background: 'url(/images/loginBackground.png) no-repeat center center fixed',
              width: '100%',
              height: '100%',
              position: 'fixed',
              zIndex: '-1',
              backgroundSize: 'cover',
            },
            loginContainer: {
              height: 'calc(100vh - 8px)',
              paddingTop: '30px',
              minHeight: '750px',
            },
            loginTitleBox: {
              paddingLeft: '24px',
              position: 'fixed',
              top: '8px',
              left: '8px',
              width: '100%',
            },
            baeLogoContainer: {
              height: '140px',
              width: '140px',
              marginBottom: '30px',
              marginLeft: 'calc(50% - 70px)',
              background: 'url(/images/BAELogo.svg)',
              backgroundSize: 'cover',
            },
            dialogPaper: {
              borderRadius: '4px',
            },
            dialogTitle: {
              fontSize: '28px',
              fontWeight: 500,
              textAlign: 'center',
            },
            dialogText: {
              color: theme.palette.text.primary,
              fontWeight: 400,
              fontSize: '22px',
            },
            dialogLink: {
              fontSize: '22px',
              fontWeight: 400,
              display: 'contents',
            },
            loginGrid: {
              marginTop: '20px',
              paddingLeft: '14px',
              paddingRight: '14px',
              paddingBottom: '20px',
            },
            gridContents: {
              display: 'contents',
            },
            headerImage: {
              width: '170px',
              height: '40px',
              marginTop: 0,
            },
            linkColor: {
              color: '#999',
            },
            justifyLeft: {
              justifyContent: 'left',
            },
          })
        }
      >
        {props.children}
      </ThemeProvider>
    </ThemeProvider>
  );
}

export default MainTheme;
